import React, { useState, useEffect, useContext } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	CircularProgress,
	Select,
	InputLabel,
	MenuItem,
	FormControl,
	FormHelperText,
	TextField,
	InputAdornment,
	IconButton,
	FormControlLabel,
	Switch
} from '@material-ui/core';

import { Visibility, VisibilityOff } from '@material-ui/icons';

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import usePlans from "../../hooks/usePlans";

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		flexWrap: "wrap",
	},
	multFieldLine: {
		display: "flex",
		"& > *:not(:last-child)": {
			marginRight: theme.spacing(1),
		},
	},

	btnWrapper: {
		position: "relative",
	},

	buttonProgress: {
		color: green[500],
		position: "absolute",
		top: "50%",
		left: "50%",
		marginTop: -12,
		marginLeft: -12,
	},
	formControl: {
		margin: theme.spacing(1),
		minWidth: 120,
	},
}));

const CompanySchema = Yup.object().shape({
	name: Yup.string()
		.min(2, i18n.t("companyModal.form.nameShort"))
		.max(50, i18n.t("companyModal.form.nameLong"))
		.required(i18n.t("companyModal.form.nameRequired")),
	// `.nullable()`: o banco permite email nulo (empresas antigas têm), e sem
	// isto o Yup quebrava só de ABRIR o modal dessas empresas — "email must be
	// a string type, but the final value was: null". Continua obrigatório para
	// salvar; o nulo só deixa de derrubar a validação na carga.
	email: Yup.string().nullable().email(i18n.t("companyModal.form.emailInvalid")).required(i18n.t("companyModal.form.emailRequired")),
	planId: Yup.string().required(i18n.t("companyModal.form.planRequired")),
	numberAttendants: Yup.number(),
	numberConections: Yup.number(),
});

const CompanyModal = ({ open, onClose, companyId }) => {
	const classes = useStyles();
	const { list } = usePlans();

	const initialState = {
		name: "",
		email: "",
		passwordDefault: "",
		numberAttendants: 1,
		numberConections: 1,
		status: false,
		planId: "",
		// #4128 — dono da empresa (isento da regra de horario de trabalho)
		ownerId: ""
	};

	const [company, setCompany] = useState(initialState);
	const [showPassword, setShowPassword] = useState(false);
	const [plans, setPlans] = useState([]);
	// #4128 — usuários da empresa, para escolher o dono (isento da regra de
	// horário de trabalho). Só o super admin vê e altera este campo.
	const [companyUsers, setCompanyUsers] = useState([]);

	useEffect(() => {
		const fetchData = async () => {
			try {
				// Buscar lista de planos. O hook devolve o ARRAY direto —
				// `plansData.plans` não existe e deixava o select SEMPRE vazio,
				// mesmo com planos cadastrados. Aceita as duas formas.
				const plansData = await list();
				setPlans(Array.isArray(plansData) ? plansData : plansData?.plans || []);

				// Buscar dados da empresa se estiver editando
				if (companyId) {
					const { data } = await api.get(`/companies/listPlan/${companyId}`);
					setCompany(prevState => {
						// Campos nulos vindos do banco viram "" — o Yup nao aceita null
						// num string() ("email must be a string type, but the final
						// value was: null") e o TextField do MUI passaria de
						// nao-controlado para controlado no meio do ciclo.
						const normalizado = Object.fromEntries(
							Object.entries(data).map(([k, v]) => [k, v === null ? "" : v])
						);
						return { ...prevState, ...normalizado };
					});

					// Usuários daquela empresa para o select de dono. A rota só
					// devolve outra empresa para super admin (isolamento).
					try {
						const { data: users } = await api.get("/users/list", {
							params: { companyId }
						});
						setCompanyUsers(users || []);
					} catch {
						setCompanyUsers([]);
					}
				}
			} catch (err) {
				toastError(err);
			}
		};

		if (open) {
			fetchData();
		} else {
			setCompanyUsers([]);
		}
	}, [companyId, open]);

	const handleClose = () => {
		onClose();
		setCompany(initialState);
	};

	const handleSaveCompany = async values => {
		// #4128 — o dono vai por rota própria (PUT /companies/:id/owner,
		// protegida por isSuper). Não entra no update geral da empresa, senão
		// qualquer admin poderia se auto-promover a isento da regra de horário.
		const { ownerId, ...companyData } = values;
		try {
			if (companyId) {
				// O dono vai PRIMEIRO: são duas requisições, cada uma com sua
				// própria transação no servidor — não há rollback entre elas.
				// Salvando o dono antes, um erro dele (usuário de outra
				// empresa, ou sem permissão) aborta tudo com o cadastro ainda
				// intacto, em vez de deixar metade gravada.
				if (ownerId !== company.ownerId) {
					await api.put(`/companies/${companyId}/owner`, {
						ownerId: ownerId || null
					});
				}

				await api.put(`/companies/${companyId}`, companyData);
			} else {
				await api.post("/companies", companyData);
			}
			toast.success(i18n.t("companyModal.success"));
		} catch (err) {
			toastError(err);
		}
		handleClose();
	};

	return (
		<div className={classes.root}>
			<Dialog
				open={open}
				onClose={handleClose}
				maxWidth="xs"
				fullWidth
				scroll="paper"
			>
				<DialogTitle id="form-dialog-title">
					{companyId
						? `${i18n.t("companyModal.title.edit")}`
						: `${i18n.t("companyModal.title.add")}`}
				</DialogTitle>
				<Formik
					initialValues={company}
					enableReinitialize={true}
					validationSchema={CompanySchema}
					onSubmit={(values, actions) => {
						setTimeout(() => {
							handleSaveCompany(values);
							actions.setSubmitting(false);
						}, 400);
					}}
				>
					{({ values, touched, errors, isSubmitting }) => (
						<Form>
							<DialogContent dividers>
								<div className={classes.multFieldLine}>
									<Field
										as={TextField}
										label={i18n.t("companyModal.form.name")}
										autoFocus
										name="name"
										error={touched.name && Boolean(errors.name)}
										helperText={touched.name && errors.name}
										variant="outlined"
										margin="dense"
										fullWidth
									/>
								</div>
								<div className={classes.multFieldLine}>
									<FormControlLabel
										control={
											<Field
												as={Switch}
												color="primary"
												name="status"
												checked={values.status}
											/>
										}
										label={i18n.t("companyModal.form.active")}
									/>
								</div>
								<div className={classes.multFieldLine}>
									<Field
										as={TextField}
										label={i18n.t("companyModal.form.email")}
										name="email"
										error={touched.email && Boolean(errors.email)}
										helperText={touched.email && errors.email}
										variant="outlined"
										margin="dense"
										fullWidth
									/>
								</div>
								<div className={classes.multFieldLine}>
									<Field
										as={TextField}
										name="passwordDefault"
										variant="outlined"
										margin="dense"
										label={i18n.t("companyModal.form.passwordDefault")}
										error={touched.passwordDefault && Boolean(errors.passwordDefault)}
										helperText={touched.passwordDefault && errors.passwordDefault}
										type={showPassword ? 'text' : 'password'}
										InputProps={{
											endAdornment: (
												<InputAdornment position="end">
													<IconButton
														aria-label="toggle password visibility"
														onClick={() => setShowPassword((e) => !e)}
													>
														{showPassword ? <VisibilityOff /> : <Visibility />}
													</IconButton>
												</InputAdornment>
											)
										}}
										fullWidth
									/>
								</div>
								<div className={classes.multFieldLine}>
									<FormControl fullWidth variant="outlined" margin="dense" error={touched.planId && Boolean(errors.planId)}>
										<InputLabel>{i18n.t("companyModal.form.plan")}</InputLabel>
										<Field
											as={Select}
											name="planId"
											label={i18n.t("companyModal.form.plan")}
											error={touched.planId && Boolean(errors.planId)}
										>
											<MenuItem value="">
												<em>{i18n.t("companyModal.form.planSelect")}</em>
											</MenuItem>
											{plans.map((plan) => (
												<MenuItem key={plan.id} value={plan.id}>
													{plan.name} - R$ {plan.amount ? plan.amount.toLocaleString('pt-br', { minimumFractionDigits: 2 }) : '0,00'}
												</MenuItem>
											))}
										</Field>
										{touched.planId && errors.planId && (
											<div style={{ color: '#f44336', fontSize: '0.75rem', marginTop: '3px', marginLeft: '14px' }}>
												{errors.planId}
											</div>
										)}
									</FormControl>
								</div>

								{/* #4128 — usuário principal (dono). Só aparece ao EDITAR: numa
								    empresa nova o dono é o usuário criado junto, definido no
								    backend. A rota é protegida por isSuper. */}
								{companyId && (
									<div className={classes.multFieldLine}>
										<FormControl fullWidth variant="outlined" margin="dense">
											<InputLabel>{i18n.t("companyModal.form.owner")}</InputLabel>
											<Field
												as={Select}
												name="ownerId"
												label={i18n.t("companyModal.form.owner")}
											>
												<MenuItem value="">
													<em>{i18n.t("companyModal.form.ownerNone")}</em>
												</MenuItem>
												{companyUsers.map((u) => (
													<MenuItem key={u.id} value={u.id}>
														{u.name} {u.email ? `(${u.email})` : ""}
													</MenuItem>
												))}
											</Field>
											<FormHelperText>
												{i18n.t("companyModal.form.ownerHelp")}
											</FormHelperText>
										</FormControl>
									</div>
								)}
								
								{/* <div className={classes.multFieldLine}>
									<Field
										as={TextField}
										label={i18n.t("companyModal.form.numberAttendants")}
										name="numberAttendants"
										error={touched.numberAttendants && Boolean(errors.numberAttendants)}
										helperText={touched.numberAttendants && errors.numberAttendants}
										variant="outlined"
										margin="dense"
										type="number"
										fullWidth
										style={
											// console.log('touched', touched)
											console.log('value', values)
										}
									/>
								</div> */}
								{/* <div className={classes.multFieldLine}>
									<Field
										as={TextField}
										label={i18n.t("companyModal.form.numberConections")}
										name="numberConections"
										error={touched.numberConections && Boolean(errors.numberConections)}
										helperText={touched.numberConections && errors.numberConections}
										variant="outlined"
										margin="dense"
										type="number"
										fullWidth
									/>
								</div> */}
							</DialogContent>
							<DialogActions>
								<Button
									onClick={handleClose}
									color="secondary"
									disabled={isSubmitting}
									variant="outlined"
								>
									{i18n.t("companyModal.buttons.cancel")}
								</Button>
								<Button
									type="submit"
									color="primary"
									disabled={isSubmitting}
									variant="contained"
									className={classes.btnWrapper}
								>
									{companyId
										? `${i18n.t("companyModal.buttons.okEdit")}`
										: `${i18n.t("companyModal.buttons.okAdd")}`}
									{isSubmitting && (
										<CircularProgress
											size={24}
											className={classes.buttonProgress}
										/>
									)}
								</Button>
							</DialogActions>
						</Form>
					)}
				</Formik>
			</Dialog>
		</div>
	);
};

export default CompanyModal;
